// src/pages/loans/DefaultedInstallmentPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Wallet } from "lucide-react";
import { ActionLoanDueMenu } from "@/components/Holizontal/ActionLoanDueMenu";
import { useTranslation } from "react-i18next";

type Branch = { id: number; name: string };
type Bdo = { id: number; name: string };

type ApiRow = {
  id: number;
  loan_id?: number;
  date?: string;
  amount?: number | string;
  payment_status?: number | string;

  CUSTOMER?: string;
  phone_number?: string;
  BRANCH?: string;
  USERNAME?: string;
  USERNAME2?: string;
};

type ApiResponse = {
  data: ApiRow[];
  meta: {
    per_page: number;
    next_cursor: string | null;
    prev_cursor: string | null;
    has_more: boolean;
  };
  anchor_id: number | null;
  date: string;
  branches: Branch[];
  // you can keep bdos in response if you want, but we’ll load per branch
  bdos?: Bdo[];
};

type MetaListResponse<T> = { data: T[] };

const PER_PAGE = 10;
const API_PATH = "/actions/loans/installment-defaulted";

function parseAmount(a: ApiRow["amount"]) {
  if (a == null) return 0;
  if (typeof a === "number") return a;
  const n = Number(String(a).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function statusLabel(v: ApiRow["payment_status"]): "Due" | "Paid" | "Partial" {
  const n = Number(v);
  if (n === 1) return "Paid";
  if (n === 2) return "Partial";
  return "Due";
}

function useDebounced<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function DefaultedInstallmentPage() {
  const { t } = useTranslation();

  // filters
  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q, 350);

  const [branchId, setBranchId] = useState<string>("");
  const [bdoId, setBdoId] = useState<string>("");

  // loading + error
  const [bootLoading, setBootLoading] = useState(true);
  const [loading, setLoading] = useState(false); // list loading
  const [metaLoading, setMetaLoading] = useState(false); // branches meta loading
  const [bdoLoading, setBdoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // data
  const [rows, setRows] = useState<ApiRow[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [bdos, setBdos] = useState<Bdo[]>([]);

  // cursor paging
  const [cursor, setCursor] = useState<string | null>(null);
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);

  // 1) Load branches once (preferred)
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setMetaLoading(true);
        const res = await apiFetch<MetaListResponse<Branch>>("/actions/loans/meta/branches");
        if (!alive) return;
        setBranches(res.data ?? []);
      } catch {
        if (!alive) return;
        setBranches([]);
      } finally {
        if (alive) setMetaLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // 2) When branch changes: reset BDO & load BDOs for that branch (✅ select branch first)
  useEffect(() => {
    let alive = true;

    setBdoId("");
    setBdos([]);

    if (!branchId) return;

    (async () => {
      try {
        setBdoLoading(true);
        const res = await apiFetch<MetaListResponse<Bdo>>(`/actions/loans/meta/bdos?branch_id=${branchId}`);
        if (!alive) return;
        setBdos(res.data ?? []);
      } catch {
        if (!alive) return;
        setBdos([]);
      } finally {
        if (alive) setBdoLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [branchId]);

  // reset paging when filters change
  useEffect(() => {
    setCursor(null);
    setAnchorId(null);
  }, [qDebounced, branchId, bdoId]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", String(PER_PAGE));

    if (qDebounced.trim()) params.set("q", qDebounced.trim());
    if (branchId) params.set("branch_id", branchId);

    // ✅ only send bdo_id if branch selected (prevents invalid filtering)
    if (branchId && bdoId) params.set("bdo_id", bdoId);

    if (cursor) params.set("cursor", cursor);
    if (anchorId != null) params.set("anchor_id", String(anchorId));

    return params.toString();
  }, [qDebounced, branchId, bdoId, cursor, anchorId]);

  async function load(isBoot = false) {
    if (isBoot) setBootLoading(true);
    setLoading(true);
    setError(null);

    try {
      const json = await apiFetch<ApiResponse>(`${API_PATH}?${queryString}`);

      setRows(json.data ?? []);

      // ✅ accept branches from API response too (if backend already returns it)
      if ((json.branches ?? []).length) setBranches(json.branches ?? []);

      setNextCursor(json.meta?.next_cursor ?? null);
      setPrevCursor(json.meta?.prev_cursor ?? null);
      setAnchorId((prev) => (prev == null ? json.anchor_id ?? null : prev));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
      setRows([]);
      setNextCursor(null);
      setPrevCursor(null);
    } finally {
      setLoading(false);
      if (isBoot) setBootLoading(false);
    }
  }

  // boot load
  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reload when query changes (after boot)
  useEffect(() => {
    if (bootLoading) return;
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const totals = useMemo(() => {
    const mapped = rows.map((r) => ({
      amount: parseAmount(r.amount),
      status: statusLabel(r.payment_status),
    }));

    const due = mapped.filter((x) => x.status !== "Paid").reduce((s, x) => s + x.amount, 0);
    const paid = mapped.filter((x) => x.status === "Paid").reduce((s, x) => s + x.amount, 0);

    return { count: rows.length, due, paid };
  }, [rows]);

  const resetFilters = () => {
    setQ("");
    setBranchId("");
    setBdoId("");
    setCursor(null);
    setAnchorId(null);
  };

  const goNextPage = () => nextCursor && !loading && setCursor(nextCursor);
  const goPrevPage = () => prevCursor && !loading && setCursor(prevCursor);

  // ✅ Loader like your LoanDueNextPage
  const TopLoader = () =>
    loading ? (
      <div className="sticky top-0 z-10">
        <div className="h-1 w-full bg-muted overflow-hidden rounded">
          <motion.div
            className="h-full bg-primary"
            initial={{ x: "-100%" }}
            animate={{ x: "100%" }}
            transition={{ repeat: Infinity, duration: 1.0, ease: "linear" }}
          />
        </div>
      </div>
    ) : null;

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-4">
      <header className="border-b border-border bg-card sticky top-0 z-30 mb-3">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <Wallet className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">{t("defaultedInstallment.title")}</h1>
              <p className="text-xs text-muted-foreground">Defaulted installments (filter by branch/BDO).</p>
            </div>
          </div>
        </div>
      </header>

      <ActionLoanDueMenu />

      <TopLoader />

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Search</div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name / phone..."
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Branch</div>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              disabled={metaLoading || loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                (metaLoading || loading) && "opacity-60 cursor-not-allowed"
              )}
            >
              <option value="">{metaLoading ? "Loading branches..." : "All Branches"}</option>
              {branches.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">BDO</div>
            <select
              value={bdoId}
              onChange={(e) => setBdoId(e.target.value)}
              disabled={!branchId || bdoLoading || loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                (!branchId || bdoLoading || loading) && "opacity-60 cursor-not-allowed"
              )}
            >
              <option value="">
                {!branchId ? "Select Branch first" : bdoLoading ? "Loading BDOs..." : "All BDOs"}
              </option>
              {bdos.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
          <div className="flex flex-wrap gap-2 text-sm">
            <div className="px-3 py-1 rounded-full bg-muted">
              Total: <span className="font-semibold">{totals.count}</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-muted">
              Due: <span className="font-semibold">{helper.formatMoneyKES(totals.due)}</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-muted">
              Paid: <span className="font-semibold">{helper.formatMoneyKES(totals.paid)}</span>
            </div>
            {loading ? <div className="px-3 py-1 rounded-full bg-muted text-muted-foreground">Loading…</div> : null}
          </div>

          <button
            onClick={resetFilters}
            disabled={loading}
            className={cn(
              "h-10 px-3 rounded-md border border-border text-sm font-medium",
              loading ? "opacity-60 cursor-not-allowed" : "hover:bg-muted"
            )}
          >
            Reset
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
      </div>

      {/* Data section */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 relative">
        {/* ✅ Overlay loader like your other page */}
        {loading && (
          <div className="absolute inset-0 z-10 rounded-xl bg-background/60 backdrop-blur-[1px] flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
              <motion.span
                className="h-4 w-4 rounded-full border-2 border-muted-foreground border-t-transparent"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}
              />
              Loading defaulted installments...
            </div>
          </div>
        )}

        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Branch</th>
                  <th className="px-4 py-3 font-semibold">BDO</th>
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">Loan ID</th>
                  <th className="px-4 py-3 font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>

              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      No defaulted installments found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const st = statusLabel(r.payment_status);
                    return (
                      <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                        <td className="px-4 py-3">{r.BRANCH ?? "-"}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{r.USERNAME ?? "-"}</div>
                          <div className="text-xs text-muted-foreground">{r.USERNAME2 ?? ""}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{r.CUSTOMER ?? "-"}</div>
                          <div className="text-xs text-muted-foreground">{r.phone_number ?? "-"}</div>
                        </td>
                        <td className="px-4 py-3">{r.loan_id ?? "-"}</td>
                        <td className="px-4 py-3">{helper.formatMoneyKES(parseAmount(r.amount))}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2 py-1 rounded-full text-xs bg-muted">{st}</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {!loading && rows.length === 0 ? (
            <div className="rounded-lg border border-border p-5 text-center text-sm text-muted-foreground">
              No defaulted installments found.
            </div>
          ) : (
            rows.map((r) => {
              const st = statusLabel(r.payment_status);
              const amount = parseAmount(r.amount);

              return (
                <div key={r.id} className="rounded-xl border border-border bg-background p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{r.CUSTOMER ?? "Unknown"}</div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-1">
                        <span className="truncate">{r.phone_number ?? "-"}</span>
                        <span>•</span>
                        <span className="truncate">{r.BRANCH ?? "-"}</span>
                        {r.loan_id != null && (
                          <>
                            <span>•</span>
                            <span className="truncate">Loan #{r.loan_id}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <span
                      className={cn(
                        "shrink-0 inline-flex px-2 py-1 rounded-full text-xs border",
                        st === "Paid"
                          ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-600"
                          : st === "Partial"
                          ? "bg-amber-500/10 border-amber-500/20 text-amber-700"
                          : "bg-red-500/10 border-red-500/20 text-red-600"
                      )}
                    >
                      {st}
                    </span>
                  </div>

                  <div className="mt-3">
                    <div className="text-xs text-muted-foreground">Amount</div>
                    <div className="font-bold">{helper.formatMoneyKES(amount)}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {(r.USERNAME ?? "-") + (r.USERNAME2 ? ` & ${r.USERNAME2}` : "")}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Paging */}
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            onClick={goPrevPage}
            disabled={!prevCursor || loading}
            className={cn(
              "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
              !prevCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
            )}
          >
            <ArrowLeft className="h-4 w-4" />
            Prev
          </button>

          <button
            onClick={goNextPage}
            disabled={!nextCursor || loading}
            className={cn(
              "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
              !nextCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
            )}
          >
            Next
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}